/**
 * _walkthrough_run.js: the first-run walkthrough's run rules, kept pure so
 * walkthrough_controller.js only gathers facts and acts on the answer. Not a
 * controller: the underscore keeps esm/index.js from registering it.
 *
 * Phases (walkthrough_controller.js owns the current one):
 *   idle     nothing running; an entry line starts or resumes
 *   offer    the first-run offer card is up
 *   step     a card is up: a real control lit with the arrow, or a
 *            preview of what the client gets with the card's own Next
 *   fill     the fill is typing the sample into the quote form
 *   armed    the fill is done typing; Create Quote is lit
 *   saving   the sample quote is submitting
 *   detail   opening the saved sample's real quote page
 *   client   the client view is open from the link in the text
 *   pending  back from the client view, waiting for a page for the
 *            checkpoint, or after the payment is confirmed, for Chip
 *   checkpoint  the card between the chapters: Keep going or Later
 *   submitting  a lit real form (an Update Status item) is posting
 *   chat     Chip is open after its step
 *   end      the end card is up
 *
 * The verdicts answer what a navigation or a tap means in a phase; the calm
 * check and the memo gate the offer so it never lands on a busy screen and
 * never shows twice on one device.
 */

/**
 * The real control the walkthrough calls `name`: whatever carries
 * data-walkthrough="<name>" in its template. Every control a step lights is
 * found this way and no other, so a layout change (a button moved, restyled,
 * reworded, put in a menu) keeps working as long as the mark goes with it.
 * The names are WALKTHROUGH_TARGETS (models/public/activity_event.py);
 * tests/test_walkthrough_targets.py renders each page the walkthrough visits
 * and fails naming the step and the template whose mark is missing. A
 * control with copies for different widths marks each; the first with a box
 * is lit (firstVisible).
 */
export function target(name) {
    return `[data-walkthrough="${name}"]`;
}

/** The mark `selector` looks for (target), for reporting a control that is missing; null for none. */
export function targetName(selector) {
    return /data-walkthrough="([a-z_]+)"/.exec(selector)?.[1] ?? null;
}

/**
 * A missing control's name in words ('start_job' -> 'Start Job'), for the
 * card's label when the control itself, whose own words it normally shows,
 * is not on the page.
 */
export function markLabel(name) {
    return (name || '').split('_').filter(Boolean).map((word) => word[0].toUpperCase() + word.slice(1)).join(' ');
}

/**
 * The app's everyday New Quote: the dashboard agenda's quick action
 * (components/dashboard/agenda.html), the first with a box in DOM order.
 * The quotes page's own buttons (quotes.html) carry the mark too, for a
 * layout that hides the agenda's. The checklist's create item is left out:
 * the run teaches the control people will use every day.
 */
export const QUOTE_FORM_OPENERS = target('new_quote');

/**
 * The toggle of the real dropdown menu (a Send or Update Status menu,
 * components/detail/_action_tile_grid.html and _action_button_row.html)
 * that holds the control `item` names.
 */
export function menuToggle(item) {
    return `:is(.dropdown, .btn-group):has(> .dropdown-menu ${item}) > [data-bs-toggle="dropdown"]`;
}

/** Matches control `item` where it shows, or the toggle of the closed menu holding it. */
export function shownOrInMenu(item) {
    return `${item}, ${menuToggle(item)}`;
}

/**
 * The Send menu's Email and SMS items on a quote's or an invoice's page
 * (quote_status_actions.html, invoice_status_actions.html). A detail page
 * holds only its own document's actions, so the mark alone is enough.
 */
export const SEND_CONTROLS = { email: target('send_email'), sms: target('send_sms') };

/**
 * The real controls the steps on a saved quote light, for quote `id`: its
 * row's link to its page (the list row the save prepends, among others, so
 * named by its address), its Add Photo (_add_photo_menu.html: the menu
 * toggle, or the iOS label), and its Send menu's Email and SMS items.
 */
export function quoteControls(id) {
    return {
        row: `${target('quote_row')}[href="/quote/${id}"]`,
        add_photo: target('add_photo'),
        ...SEND_CONTROLS,
    };
}

/**
 * What each document's send steps are called: the PDF, the Send menu lit for
 * email, its Email item, the email shown, the Send menu lit for a text, its
 * SMS item, the text whose link opens the client's view; and the steps those
 * card states count towards (email, text, view).
 */
export const SEND_STATES = {
    quote: {
        pdf: 'email_quote', send: 'email_quote_send', item: 'email_quote_item', shown: 'email_quote_shown',
        text: 'text_quote', textItem: 'text_quote_item', view: 'client_view',
        steps: { email: 'email_quote', text: 'text_quote', view: 'client_view' },
    },
    invoice: {
        pdf: 'send_invoice_pdf', send: 'send_invoice_send', item: 'send_invoice_item', shown: 'send_invoice_shown',
        text: 'send_invoice_text', textItem: 'send_invoice_text_item', view: 'invoice_client_view',
        steps: { email: 'send_invoice', text: 'send_invoice', view: 'invoice_client_view' },
    },
};

/**
 * Chapter 2's real controls: each Update Status item (quote, job and invoice
 * *_status_actions.html), the banner's View Job and View Invoice (the same
 * templates' _banner_action), the job's Schedule (job_status_actions.html)
 * and the schedule modal's save (schedule_event_modal.html), and the
 * client's I've paid on the owner's invoice preview (invoice_public_view.html).
 */
export const CHAPTER_TWO = {
    accept: target('accept_quote'),
    viewJob: target('view_job'),
    schedule: target('schedule_job'),
    saveVisit: target('save_visit'),
    start: target('start_job'),
    finish: target('finish_job'),
    viewInvoice: target('view_invoice'),
    paid: target('client_paid'),
    confirm: target('confirm_paid'),
};

/** The page each chapter 2 step starts on; the run walks there by the banners' links. */
export const STEP_PAGE = {
    accept_quote: 'quote', schedule_job: 'quote', start_job: 'job', finish_job: 'job',
    send_invoice: 'job', invoice_client_view: 'invoice', confirm_paid: 'invoice',
};

/** `{kind, id}` of the quote, job or invoice detail page at `pathname`, else null. */
export function detailPage(pathname) {
    const match = /^\/(quote|job|invoice)\/(\d+)\/?$/.exec(pathname);
    return match ? { kind: match[1], id: Number(match[2]) } : null;
}

/** `{kind, id}` of the client's view of a quote or invoice at `pathname`, else null. */
export function clientPage(pathname) {
    const match = /^\/(quote|invoice)\/(\d+)\/client-preview\/?$/.exec(pathname);
    return match ? { kind: match[1], id: Number(match[2]) } : null;
}

/**
 * Whether `pathname` is one of the run's own pages: the dashboard, or the
 * sample's quote, job or invoice page or a client's view of it. An id the
 * run has not learned yet (`null`) admits any page of that kind. Arriving
 * anywhere else ends the run.
 */
export function onRunPage(pathname, { dashboard, quoteId = null, jobId = null, invoiceId = null }) {
    if (pathname === dashboard) return true;
    const page = detailPage(pathname) || clientPage(pathname);
    if (!page) return false;
    const known = { quote: quoteId, job: jobId, invoice: invoiceId }[page.kind];
    return known === null || known === undefined || known === page.id;
}

/**
 * Where chapter 2 `step` goes on on a page of `kind`, when the walk has
 * already passed the page it starts on (STEP_PAGE): booking the visit is the
 * job's Schedule once on the job, and sending the invoice its PDF once on the
 * invoice. Null when the step has nothing to do there.
 */
export function stepHere(step, kind) {
    return { schedule_job: { job: 'schedule_job_open' }, send_invoice: { invoice: 'send_invoice_pdf' } }[step]?.[kind] ?? null;
}

/** The link that walks from page `kind` towards `target` (quote, job, invoice), or null. */
export function pathTowards(kind, target) {
    const order = ['quote', 'job', 'invoice'];
    if (order.indexOf(kind) < 0 || order.indexOf(kind) >= order.indexOf(target)) return null;
    return kind === 'quote' ? CHAPTER_TWO.viewJob : CHAPTER_TWO.viewInvoice;
}

/** The day after `now` as YYYY-MM-DD, in local time: the sample visit's day. */
export function tomorrow(now) {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
}

/** Present only on a dashboard rendered with the offer due (dashboard_first_run.html). */
const OFFER_FLAG = 'template[data-walkthrough-offer]';

/** Open UI the offer must not cover: a sheet or modal, a toast, the chat. */
const BUSY_SELECTORS = ['.modal.show', '.unified-notification', '.pchat--open'];

/**
 * The first element under `root` matching `selector`, in DOM order, that has
 * a box. A control hidden in this shell or layout measures 0 x 0 and is
 * skipped, so the spotlight never lights a sliver in the corner. One inside
 * `outside` is skipped too: step 1 passes '.dropdown-menu', since the ring's
 * menu closes on the very tap that started the run, taking its link with it.
 */
export function firstVisible(root, selector, { outside = null } = {}) {
    for (const element of root.querySelectorAll(selector)) {
        if (outside && element.closest(outside)) continue;
        if (hasBox(element)) return element;
    }
    return null;
}

/** True while `element` takes up space: a control hidden at this width, or in a closed menu, has no box. */
export function hasBox(element) {
    const rect = element?.getBoundingClientRect();
    return Boolean(rect && rect.width > 0 && rect.height > 0);
}

/**
 * What a Turbo visit to `url` does to the run: 'ignore', 'keep', 'advance'
 * (leaving the client view completes step 3), 'dismiss' (offer or end card
 * leaves, no event) or 'close' (reported as a navigation close).
 *
 * `swapping` is a page-check same-URL replace, which never ends a run.
 * `ownUrl` is the visit the run made itself (the dashboard for step 1, the
 * pill for step 3) and must be absolute, as `url` from the event is; a
 * restore visit passes null, so Back is judged by phase alone.
 */
export function navigationVerdict({ phase, swapping, url, ownUrl }) {
    if (phase === 'idle') return 'ignore';
    if (swapping) return 'keep';
    if (ownUrl && url === ownUrl) return 'keep';
    if (phase === 'client') return 'advance';
    if (phase === 'pending' || phase === 'chat') return 'keep';
    if (phase === 'offer' || phase === 'end') return 'dismiss';
    return 'close';
}

/**
 * What a captured tap does: 'start' (an entry line while idle), 'ignore',
 * 'dismiss' (the end card), 'card' (the card's own buttons act), 'not_now'
 * (off the offer card), 'later' (off the chapter checkpoint), 'through' (the
 * lit target gets the click), 'warn' (a first tap outside: the tap is held
 * and the card says how to carry on or leave) or 'tap_outside' (a tap outside
 * once `warned`: closes the run). While the sample is typing the lit field
 * is not tappable, and an entry line tapped mid-run is a tap outside. A lit
 * target inside the card (the link in the text preview) passes through.
 */
export function tapVerdict({ phase, inCard, onTarget, onEntry, warned = false }) {
    if (phase === 'idle') return onEntry ? 'start' : 'ignore';
    if (['saving', 'submitting', 'detail', 'client', 'pending', 'chat'].includes(phase)) return 'ignore';
    if (phase === 'end') return 'dismiss';
    if (onTarget && ['step', 'armed'].includes(phase)) return 'through';
    if (inCard) return 'card';
    if (phase === 'offer') return 'not_now';
    if (phase === 'checkpoint') return 'later';
    return warned ? 'tap_outside' : 'warn';
}

/**
 * True when the offer may show: nobody typing, nothing else open or arriving,
 * the page visible and online, and the offer flag still on the page. `dialog`
 * is the walkthrough's own, which may already be open. A child in
 * #onboard-prompt is the send prompt streamed in after a save
 * (application/turbo/quote_created.py).
 */
export function screenIsCalm(doc, win, dialog) {
    if (isEditable(doc.activeElement)) return false;
    if (BUSY_SELECTORS.some((selector) => doc.querySelector(selector))) return false;
    if (Array.from(doc.querySelectorAll('dialog[open]')).some((open) => open !== dialog)) return false;
    if (doc.querySelector('#onboard-prompt')?.childElementCount > 0) return false;
    if (win.chippiePageCheck?.isSwapping()) return false;
    if (doc.visibilityState !== 'visible') return false;
    if (win.navigator.onLine === false) return false;
    return Boolean(doc.querySelector(OFFER_FLAG));
}

/** A field someone may be typing in (page-check.js counts the same as busy). */
function isEditable(element) {
    return Boolean(element && (element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)));
}

/**
 * Resolves once every stylesheet link in `doc` has loaded (or failed), or
 * after `giveUpMs`. Turbo fires its load before a new page's stylesheets
 * finish, and they can move a control.
 */
export function stylesLoaded(doc, giveUpMs) {
    const pending = Array.from(doc.querySelectorAll('link[rel="stylesheet"]')).filter((link) => !link.sheet);
    if (pending.length === 0) return Promise.resolve();
    const each = pending.map((link) => new Promise((resolve) => {
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', resolve, { once: true });
    }));
    let timer;
    const giveUp = new Promise((resolve) => { timer = setTimeout(resolve, giveUpMs); });
    return Promise.race([Promise.all(each), giveUp]).finally(() => clearTimeout(timer));
}

/** `href` resolved against `base`, so it compares equal to a Turbo event's url. */
export function absoluteUrl(href, base) {
    return new URL(href, base).href;
}

function memoKey(userId) {
    return `chippie_walkthrough_${userId}`;
}

/**
 * This device's memo of the run for `userId` ('offered' or 'finished'), or
 * null. `storage` is window.localStorage, which may be null or throw in a
 * private window or with site data blocked; both read as no memo.
 */
export function readMemo(storage, userId) {
    try {
        return storage.getItem(memoKey(userId));
    } catch {
        return null;
    }
}

/** Store the memo for `userId`; an unusable storage stores nothing. */
export function writeMemo(storage, userId, value) {
    try {
        storage.setItem(memoKey(userId), value);
    } catch {
        // The server's progress record still stops a second offer.
    }
}
